// src/features/chales/components/ChaletCard.jsx
import React, { useState } from "react";
import PaymentModal from "./PaymentModal";
import AddCreditModal from "./AddCreditModal"; // ✅ 1. Importa o Modal de Crédito
import { Button } from "components/ui/button";
import { Edit, Trash2 } from "lucide-react";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0
  );

function ChaletCard({
  chale,
  onAddPayment,
  onRemovePayment,
  onDeleteReservation,
  onEditReservation,
  onAddCredit, // ✅ 2. Recebe a função de adicionar crédito
  canEdit,
}) {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false); // ✅ Estado para o modal de crédito
  const isAvailable = chale.isAvailable;

  const handleAddPayment = (payment) => {
    onAddPayment(chale.reservationId, payment);
  };

  const handleRemovePayment = (paymentObject) => {
    onRemovePayment(chale.reservationId, paymentObject);
  };

  // ✅ 3. Nova lógica de exclusão inteligente
  const handleDeleteClick = () => {
    if (
      !window.confirm(
        `Tem certeza que deseja cancelar a reserva do Chalé ${chale.chaletNumber}?`
      )
    ) {
      return;
    }

    // Verifica se há pagamentos realizados
    if (chale.totalPaid > 0) {
      const wishToCredit = window.confirm(
        `Esta reserva possui R$ ${formatCurrency(
          chale.totalPaid
        )} pagos.\n\nDeseja gerar um CRÉDITO para o cliente com este valor antes de excluir?`
      );

      if (wishToCredit) {
        setShowCreditModal(true); // Abre o modal para confirmar dados do crédito
        return; // Interrompe a exclusão direta
      }
    }

    // Se não tiver pagamentos ou o usuário escolher "Não" para o crédito, exclui direto
    onDeleteReservation(chale.reservationId);
  };

  // ✅ 4. Callback ao confirmar o crédito
  const handleCreditConfirm = async (creditData) => {
    if (onAddCredit) {
      await onAddCredit(creditData); // Salva o crédito
    }
    onDeleteReservation(chale.reservationId); // Exclui a reserva após salvar o crédito
    setShowCreditModal(false);
  };

  return (
    <>
      <div
        className={`rounded-lg shadow-md flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border ${
          isAvailable
            ? "bg-green-100 dark:bg-green-900/40 border-green-200 dark:border-green-800/50"
            : "bg-white dark:bg-card border-gray-100 dark:border-border"
        }`}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">
            Chalé {chale.chaletNumber}
          </h3>

          {!isAvailable && canEdit && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onEditReservation(chale)}
              >
                <Edit className="h-4 w-4 text-muted-foreground" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleDeleteClick} // ✅ Usa a nova função de clique
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          )}
        </div>

        <div className="p-4 flex-grow">
          <div className="mb-3">
            <p
              className={`font-semibold ${
                isAvailable
                  ? "text-green-900 dark:text-green-300"
                  : "text-gray-800 dark:text-gray-100"
              }`}
            >
              {chale.guestInfo}
            </p>
            {!isAvailable && chale.phoneInfo && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {chale.phoneInfo}
              </p>
            )}
          </div>

          {!isAvailable && chale.observations && (
            <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/10 rounded border-l-2 border-amber-400 dark:border-amber-500/50">
              <p className="text-xs text-amber-800 dark:text-amber-200/80 italic">
                <span className="font-semibold not-italic">Obs:</span>{" "}
                {chale.observations}
              </p>
            </div>
          )}

          {!isAvailable && (
            <div className="space-y-1">
              {chale.payments.map((p) => (
                <div
                  key={p.id}
                  className="flex justify-between items-center text-sm py-1 border-b border-gray-100 last:border-b-0"
                >
                  <span className="text-gray-600 dark:text-gray-400">
                    {p.type}:
                  </span>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900 dark:text-emerald-400">
                      {formatCurrency(p.value)}
                    </span>
                    {p.type !== "Depósito" && canEdit && (
                      <button
                        onClick={() => handleRemovePayment(p)}
                        className="ml-2 text-red-400 hover:text-red-600 font-bold text-s"
                        title="Remover este pagamento"
                      >
                        ✖
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {chale.payments.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-2">
                  Nenhum pagamento registrado.
                </p>
              )}
            </div>
          )}
        </div>

        {!isAvailable && canEdit && (
          <div className="p-4 bg-gray-50 dark:bg-card/40 border-t dark:border-green-900/30 rounded-b-lg mt-auto">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-semibold text-gray-700 dark:text-muted-foreground">
                Total Pago:
              </span>
              <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(chale.totalPaid)}
              </span>
            </div>
            <button
              onClick={() => setIsPaymentModalOpen(true)}
              className="w-full bg-primary/90 hover:bg-primary text-primary-foreground font-bold py-2 px-4 rounded-md text-sm transition-all shadow-sm shadow-primary/20"
            >
              Adicionar Pagamento
            </button>
          </div>
        )}
      </div>

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onAddPayment={handleAddPayment}
      />

      {/* ✅ 5. Renderiza o Modal de Crédito */}
      <AddCreditModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onConfirm={handleCreditConfirm}
        // Passa os dados da reserva atual para pré-preencher o modal
        initialData={{
          ...chale,
          id: chale.reservationId, // Garante que o ID esteja correto
        }}
      />
    </>
  );
}
export default ChaletCard;
